import type {
  DayTradeBotInstrumentResponseDto,
  DayTradeBotSessionStateDto,
} from '../schemas/investments/day-trade-bot.schema'

// the schema default for maxHeldLots in min-spread mode. an instrument still sitting on this
// value has no deliberate lots cap, so Σ maxHeldLots × lotValue is a meaningless denominator
// (millions) that would dilute every exposure/ROI percentage to ~0. treated as "unset". (#244)
export const MIN_SPREAD_DEFAULT_MAX_HELD_LOTS = 1000

// one-lot notional: the explicit lotPrice override, else the live market value (lotSize × price)
const perLotValue = (
  inst: DayTradeBotInstrumentResponseDto,
  state: DayTradeBotSessionStateDto | undefined
): number => inst.lotPrice ?? (state?.lotSize ?? 1) * (state?.currentPrice ?? 0)

export interface DayTradeExposure {
  // cost basis currently deployed: Σ avgEntry × lotsHeld × lotSize over held instruments. uses the
  // real avgEntry and ignores the lotPrice override, so it reflects what was actually paid —
  // openExposure + unrealized = current market value.
  openExposure: number
  // mark-to-market P&L on the open position: Σ (currentPrice − avgEntry) × lotsHeld × lotSize
  unrealized: number
  // the allocated capital base the exposure is measured against: the account-level budget when one
  // is set, else the summed per-instrument lots cap. 0 when neither is meaningful.
  maxExposure: number
  // share of the allocated base currently deployed, in percent; null when there is no meaningful
  // base to measure against
  exposurePct: number | null
  // whether a real cap exists (a money budget, or at least one deliberate lots cap)
  hasMeaningfulBase: boolean
}

// account-level exposure and unrealized P&L, derived from the live session states plus the
// instrument configs. shared by the day-trade stats cards and the daily Telegram report so the
// two can never report different numbers for the same account.
export function computeDayTradeExposure(
  sessionStates: DayTradeBotSessionStateDto[],
  instruments: DayTradeBotInstrumentResponseDto[]
): DayTradeExposure {
  const unrealized = sessionStates.reduce((sum, s) => {
    if (s.lotsHeld <= 0 || s.avgEntry == null || s.currentPrice == null) return sum
    return sum + (s.currentPrice - s.avgEntry) * s.lotsHeld * (s.lotSize ?? 1)
  }, 0)

  const stateByUid = new Map(sessionStates.map((s) => [s.instrumentUid, s]))
  let openExposure = 0
  // the lots-cap bound summed across instruments — the fallback base when no money budget is set
  let lotsCapExposure = 0
  for (const inst of instruments) {
    if (!inst.isEnabled) continue
    const state = stateByUid.get(inst.instrumentUid)
    lotsCapExposure += inst.effectiveSettings.maxHeldLots * perLotValue(inst, state)
    if (state && state.lotsHeld > 0 && state.avgEntry != null)
      openExposure += state.avgEntry * state.lotsHeld * (state.lotSize ?? 1)
  }

  // budget is a single shared pool across the whole account, so the max total open exposure is the
  // budget itself (NOT summed per instrument); it is account-level, so any instrument's effective
  // value carries it.
  const accountBudget = instruments[0]?.effectiveSettings.budget ?? 0
  const hasBudget = accountBudget > 0
  const hasRealLotsCap = instruments.some(
    (inst) =>
      inst.isEnabled && inst.effectiveSettings.maxHeldLots < MIN_SPREAD_DEFAULT_MAX_HELD_LOTS
  )
  const hasMeaningfulBase = hasBudget || hasRealLotsCap
  const maxExposure = hasBudget ? accountBudget : lotsCapExposure

  return {
    openExposure,
    unrealized,
    maxExposure,
    exposurePct: hasMeaningfulBase && maxExposure > 0 ? (openExposure / maxExposure) * 100 : null,
    hasMeaningfulBase,
  }
}

// the realized figures a return is computed from: the ledger recompute's after-tax nets plus the
// span the all-time one is averaged over
export interface DayTradeRealized {
  today: { net: number }
  allTime: { net: number }
  firstTradeAt: Date | null
  elapsedDays: number
}

export interface DayTradeReturns {
  // the allocated capital base both rates divide by; 0 when there is no meaningful cap
  capitalBase: number
  // today's same-day return in percent; null when there is no base or no ledger figures
  todayPct: number | null
  // the all-time return spread over the calendar days since the first fill — an average DAILY
  // rate, directly comparable with todayPct. null before the first trade.
  avgDailyPct: number | null
}

// ROI on the allocated capital base. lives beside computeDayTradeExposure so the bot page's stats
// cards and the dashboard widget can never report different percentages for the same account.
export function computeDayTradeReturns(
  exposure: DayTradeExposure,
  realized: DayTradeRealized | undefined
): DayTradeReturns {
  const capitalBase = exposure.hasMeaningfulBase ? exposure.maxExposure : 0
  if (capitalBase <= 0 || realized == null) {
    return { capitalBase: Math.max(0, capitalBase), todayPct: null, avgDailyPct: null }
  }

  return {
    capitalBase,
    todayPct: (realized.today.net / capitalBase) * 100,
    avgDailyPct:
      realized.firstTradeAt != null && realized.elapsedDays > 0
        ? (realized.allTime.net / capitalBase / realized.elapsedDays) * 100
        : null,
  }
}
